import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Icon, Layer, Loading, Pane } from '@folio/stripes-components';
import { useHistory, useParams } from 'react-router-dom';
import { IfPermission } from '@folio/stripes-core';
import { FormattedMessage, useIntl } from 'react-intl';
import { ViewMetaData } from '@folio/stripes/smart-components';
import useTemplate from '../../../../hooks/useTemplate';
import css from '../../../../shared-styles/sharedStyles.css';
import TemplateInfoAccordion from './TemplateInfoAccordion';
import TemplateFieldsAccordion from './TemplateFieldsAccordion';
import DeleteTemplateModal from '../DeleteTemplateModal';

function ViewTemplate({ basePath }) {
  const [openDeleteModal, setOpenDeleteModal] = useState(false);

  const history = useHistory();
  const params = useParams();
  const { templates, isLoading } = useTemplate(params.templateId);
  const { locale } = useIntl();
  const localizedName = templates?.name?.find(
    (field) => field?.locale === locale,
  )?.value;

  const exportTemplate = (filename, data, fileType) => {
    const blob =
      fileType === 'json'
        ? new Blob([data], { type: 'application/json' })
        : new Blob([data], { type: 'text/csv' });

    if (window.navigator.msSaveOrOpenBlob) {
      window.navigator.msSaveBlob(blob, filename);
    } else {
      const elem = window.document.createElement('a');
      elem.href = window.URL.createObjectURL(blob);
      elem.download = filename;
      document.body.appendChild(elem);
      elem.click();
      document.body.removeChild(elem);
    }
  };

  const getActionMenu = ({ onToggle }) => {
    return (
      <div>
        <IfPermission perm="ui-plugin-inventory-templates.inventoryTemplates.edit">
          <Button
            id="inventory-templates-plugin-view-template-edit-btn"
            buttonStyle="dropdownItem"
            to={`${basePath}/template-builder/${params.templateId}/edit`}
          >
            <Icon icon="edit">
              <FormattedMessage
                id="ui-plugin-inventory-templates.buttons.edit"
                defaultMessage="Edit template"
              />
            </Icon>
          </Button>
        </IfPermission>
        <hr />

        <IfPermission perm="ui-plugin-inventory-templates.inventoryTemplates.clone">
          <Button
            id="inventory-templates-plugin-view-template-clone-btn"
            buttonStyle="dropdownItem"
            to={`${basePath}/template-builder/${params.templateId}/duplicate`}
          >
            <Icon icon="duplicate">
              <FormattedMessage
                id="ui-plugin-inventory-templates.buttons.clone"
                defaultMessage="Clone template"
              />
            </Icon>
          </Button>
        </IfPermission>

        <IfPermission perm="ui-plugin-inventory-templates.inventoryTemplates.export">
          <Button
            id="inventory-templates-plugin-view-template-export-btn"
            buttonStyle="dropdownItem"
            onClick={() => {
              onToggle();
              exportTemplate(
                localizedName,
                JSON.stringify(templates, null, ' '),
                'json',
              );
            }}
          >
            <Icon icon="save">
              <FormattedMessage
                id="ui-plugin-inventory-templates.buttons.exprt"
                defaultMessage="Export template"
              />
            </Icon>
          </Button>
        </IfPermission>

        <hr />
        <IfPermission perm="ui-plugin-inventory-templates.inventoryTemplates.delete">
          <Button
            id="inventory-templates-plugin-view-template-delete-btn"
            buttonStyle="dropdownItem"
            onClick={() => {
              onToggle();
              setOpenDeleteModal(true);
            }}
            disabled={templates.isDefault}
          >
            <Icon icon="trash">
              <FormattedMessage
                id="ui-plugin-inventory-templates.buttons.delete"
                defaultMessage="Delete template"
              />
            </Icon>
          </Button>
        </IfPermission>
      </div>
    );
  };

  return (
    <Layer contentLabel="View inventory template" isOpen>
      {isLoading && (
        <div className={css.loading}>
          <Loading size="xlarge" />
        </div>
      )}
      {!isLoading && (
        <Pane
          actionMenu={getActionMenu}
          id="inventory-templates-plugin-view-template-pane"
          defaultWidth="fill"
          paneTitle={
            <FormattedMessage
              id="ui-plugin-inventory-templates.viewTemplate.pane.paneTitle"
              values={{ template: localizedName }}
              defaultMessage="View {template} template"
            />
          }
          dismissible
          onClose={() => history.push(`${basePath}/list-templates`)}
        >
          <div className={css.formStyle}>
            <ViewMetaData metadata={templates.metadata} />
            <TemplateInfoAccordion template={templates} />
            <TemplateFieldsAccordion template={templates} />
          </div>
        </Pane>
      )}
      <DeleteTemplateModal
        open={openDeleteModal}
        onClose={() => setOpenDeleteModal(false)}
        templateId={params.templateId}
        goBack={() => history.push(`${basePath}/list-templates`)}
      />
    </Layer>
  );
}

ViewTemplate.propTypes = {
  basePath: PropTypes.string,
};

export default ViewTemplate;
